import { create } from '@bufbuild/protobuf';

import { AuditlogEventType } from './espb/auditlog_event_type_pb';
import { AuthenticatorType } from './espb/authenticator_type_pb';
import { ChannelRecipient } from './espb/channel_service_pb';
import { CredentialType } from './espb/credential_type_pb';
import { LicenseType } from './espb/license_type_pb';
import { VaultLockCreationDataSchema, VaultProfileLockSchema } from './espb/locks_pb';
import type { VaultLockCreationData, VaultProfileLock } from './espb/locks_pb';
import { MaintenanceTask } from './espb/maintenance_task_pb';
import { OrganizationType } from './espb/organization_type_pb';
import { PrimaryLoginDevice } from './espb/primary_login_device_pb';
import { ProfileRegenerateDataSchema } from './espb/profile_pb';
import type { ProfileRegenerateData } from './espb/profile_pb';
import { ProfileType } from './espb/profile_type_pb';
import { RegistrationInfoStatus } from './espb/registration_info_status_pb';
import {
  ExportPermission,
  ChildOrganizationAccess,
  MaximumAssignableVaultPermission,
} from './espb/role_pb';
import { RoleType } from './espb/role_type_pb';
import { SessionType } from './espb/session_type_pb';
import { SubscriptionPlan } from './espb/subscription_plan_pb';
import { ReportPageProblem, SupportMessageType } from './espb/support_service_pb';
import { SubscriptionError } from './espb/sync_update_pb';
import { UserConfirmationType } from './espb/user_confirmation_type_pb';
import { VaultMemberSyncMode } from './espb/vault_member_sync_mode_pb';
import { VaultMessageType } from './espb/vault_message_type_pb';
import { VaultType } from './espb/vault_type_pb';
import { WebauthnPrfSupportStatus } from './espb/webauthn_prf_support_status_pb';
import { requireFieldNotEmpty } from './grpcUtils';

export { Achievement } from './espb/achievements_pb';
export { AuditlogEventType, AuditlogEventTypeGroup } from './espb/auditlog_event_type_pb';
export { AuthenticatorType } from './espb/authenticator_type_pb';
export { ChildOrganizationAccess } from './espb/role_pb';
export { CredentialType } from './espb/credential_type_pb';
export { ErrorCode } from './espb/error_codes_pb';
export { MaintenanceTask } from './espb/maintenance_task_pb';
export { OrganizationType } from './espb/organization_type_pb';
export { ProfileType } from './espb/profile_type_pb';
export { ReportPageProblem } from './espb/support_service_pb';
export { SessionType } from './espb/session_type_pb';
export { PrimaryLoginDevice } from './espb/primary_login_device_pb';
export { SubscriptionError } from './espb/sync_update_pb';
export { SubscriptionPlan } from './espb/subscription_plan_pb';
export { SyncUpdateField } from './espb/sync_update_pb';
export { UserConfirmationResultType } from './espb/user_confirmation_result_type_pb';
export { UserConfirmationType } from './espb/user_confirmation_type_pb';
export { RoleType } from './espb/role_type_pb';
export { UserType } from './espb/user_type_pb';
export { VaultMessageType } from './espb/vault_message_type_pb';
export { VaultType } from './espb/vault_type_pb';
export { VaultMemberSyncMode } from './espb/vault_member_sync_mode_pb';
export { RegistrationInfoStatus } from './espb/registration_info_status_pb';
export { WebauthnPrfSupportStatus } from './espb/webauthn_prf_support_status_pb';
export { ClientType } from './espb/client_type_pb';
export { LicenseType } from './espb/license_type_pb';
export { SupportMessageType } from './espb/support_service_pb';
export { LoginMaintenanceCriteriaVersion } from './espb/login_maintenance_criteria_pb';
export { VaultPermissionLevel } from './espb/vault_permission_level_pb';
export type { Timestamp as GrpcTimestamp } from '@bufbuild/protobuf/wkt';

// Using flavored types.  We don't use branded types (same thing but without the
// '?' on the type), that way it's stil possible to do implicit conversions from
// non-flavored types to flavored ones.
// https://spin.atomicobject.com/2018/01/15/typescript-flexible-nominal-typing/

// https://medium.com/@KevinBGreene/surviving-the-typescript-ecosystem-branding-and-type-tagging-6cf6e516523d
export type Flavor<K, T> = K & { __flavor?: T };

export type UUID = string;
export type AuthenticatorId = Flavor<UUID, 'AuthenticatorId'>;
export type ProfileId = Flavor<UUID, 'ProfileId'>;
export type CommitId = Flavor<UUID, 'CommitId'>;
export type GenerationId = Flavor<UUID, 'GenerationId'>;
export type ProfileKeyGenerationId = Flavor<UUID, 'ProfileKeyGenerationId'>;
export type LockId = Flavor<UUID, 'LockId'>;
export type UserId = Flavor<UUID, 'UserId'>;
export type VaultId = Flavor<UUID, 'VaultId'>;
export type RoleId = Flavor<UUID, 'RoleId'>;
export type ReferrerId = Flavor<UUID, 'ReferrerId'>;
export type SessionId = Flavor<UUID, 'SessionId'>;
export type OrganizationId = Flavor<UUID, 'OrganizationId'>;
export type LoginId = Flavor<UUID, 'LoginId'>;
export type EventId = Flavor<UUID, 'EventId'>;
export type Email = string;
export type ChannelId = UUID;
export type LoginChannelId = Flavor<ChannelId, 'LoginChannelId'>;
export type UserConfirmationId = Flavor<UUID, 'UserConfirmationId'>;
export type EntraUserId = Flavor<UUID, 'EntraUserId'>;
export type EntraGroupId = Flavor<UUID, 'EntraGroupId'>;
export type GoogleWorkspaceUserId = Flavor<UUID, 'WorkspaceUserId'>;
export type VaultMessageId = Flavor<UUID, 'VaultMessageId'>;
export type AchievementId = Flavor<number, 'AchievementId'>;
export type LoginRevisionId = Flavor<UUID, 'LoginRevisionId'>;
export type ShareLinkId = Flavor<string, 'ShareLinkId'>;
export type ShareSecretId = Flavor<string, 'ShareSecretId'>;
export type EntraTenantId = Flavor<UUID, 'EntraTenantId'>;

// a type for the opaque base64Urlsafe-encoded raw webauthn id
export type WebauthnId = Flavor<string, 'WebauthnId'>;

export type HashCommitmentDto = Flavor<Uint8Array, 'HashCommitmentDto'>;
export type ClientPubKeyDto = Flavor<Uint8Array, 'ClientPubKeyDto'>;
export type PushPubKeyDto = Flavor<Uint8Array, 'PushPubKeyDto'>;
export type ChannelType = 'unlock' | 'login';

export type AuditlogAnchor = Flavor<Date, 'AuditlogAnchor'>;

export type ReportPageProblemKnownValue = Exclude<ReportPageProblem, ReportPageProblem.UNKNOWN>;

export type VaultMessageTypeKnownValue = Exclude<VaultMessageType, VaultMessageType.UNKNOWN>;

export type SubscriptionErrorKnownValue = Exclude<SubscriptionError, SubscriptionError.OK>;

export type SubscriptionPlanKnownValue = Exclude<SubscriptionPlan, SubscriptionPlan.UNKNOWN>;

export type PrimaryLoginDeviceKnownValue = Exclude<PrimaryLoginDevice, PrimaryLoginDevice.UNKNOWN>;

export type OrganizationTypeKnownValue = Exclude<OrganizationType, OrganizationType.UNKNOWN>;

export type SupportMessageTypeKnownValue = Exclude<SupportMessageType, SupportMessageType.UNKNOWN>;

export interface AuditlogWriteToken {
  __flavor?: 'AuditlogWriteToken';
  expiresAt: Date;
  token: string;
}

export interface AuditlogAdminToken {
  __flavor?: 'AuditlogAdminToken';
  expiresAt: Date;
  token: string;
}

export interface LFDOverridesAdminToken {
  __flavor?: 'LFDOverridesAdminToken';
  expiresAt: Date;
  token: string;
}

export interface SyncToken {
  __flavor?: 'SyncToken';
  expiresAt: Date;
  token: string;
}

export interface UnlockEncryptedSecretDto {
  encryptedSecret: Uint8Array;
  authenticatorId: AuthenticatorId;
}
export interface UnlockClientPubKeyDto {
  pubKey: Uint8Array;
  pubKeySignature: Uint8Array;
  signingAuthId: AuthenticatorId;
}

export interface EncryptedSecretDto {
  encryptedSecret: Uint8Array;
  authenticatorId: AuthenticatorId;
}

export interface LoginData {
  userId: UserId;
  sessionId: SessionId;
  token: string;
  tokenVersion: number;
}

/* This type represents a limit on a session unlock time. If disabled, the limit doesn't apply. If
 * enabled is true, the session unlock time is capped to the minutes field.
 * Sessions may be extended by the client up to their max unlock time (typically 2am the next day).
 */
export interface UnlockTimeLimit {
  enabled: boolean;
  minutes: number;
}

export interface RegisterUserResponseDto {
  userId: UserId;
  authenticatorDtos: AuthenticatorDto[];
  loginData: LoginData;
}

export interface GetUserResponseDto {
  id: UserId;
  email: Email;
  metaVaultId: VaultId;
  syncUpdate: SyncUpdate;
}

export interface ListAccountsResponseDto {
  users: {
    id: UserId;
    email?: Email;
    authenticators: ForeignAuthenticatorDto[];
    authenticatorBlocks: AuthenticatorBlockDto[];
  }[];
}

export interface VaultMessage {
  id: VaultMessageId;
  type: VaultMessageTypeKnownValue;
  senderVaultId: VaultId | undefined;
  encryptedData?: Uint8Array;
  backendMessage?: Uint8Array;
}

export interface VaultUpdateDto {
  readonly generationId: GenerationId;
  readonly commitDtos: CommitDto[];
  readonly profileLockDto?: VaultProfileLockDto;
  readonly adminProfileLockDto?: VaultProfileLockDto;
  readonly messagePublicKey?: Uint8Array;
  readonly serverTime: Date;
  readonly firstCommitId: CommitId;
  readonly messages: VaultMessage[];
  readonly loginMaintenanceCriteria?: LoginMaintenanceCriteriaDto;
}

export interface VaultCreationDto {
  blob: Uint8Array;
  profileLock?: VaultProfileLockDto;
  adminProfileLock?: VaultProfileLockDto;
  organizationId?: OrganizationId;
  messagePublicKey?: Uint8Array;
}

export interface VaultPairCreationDto {
  blob: Uint8Array;
  profileLock?: VaultProfileLockDto;
  adminProfileLock?: VaultProfileLockDto;
  messagePublicKey?: Uint8Array;
  associatedBlob: Uint8Array;
  associatedProfileLock?: VaultProfileLockDto;
  associatedAdminProfileLock?: VaultProfileLockDto;
  associatedMessagePublicKey?: Uint8Array;
  organizationId?: OrganizationId;
}

export interface VaultLockCreationDto {
  vaultId: VaultId;
  generationId: GenerationId;
  lock: VaultProfileLockDto;
  associatedVaultId?: VaultId;
  associatedGenerationId?: GenerationId;
  associatedLock?: VaultProfileLockDto;
}

export interface RegeneratedProfileDto {
  profile: ProfileDto;
  vaultLocks: VaultLockCreationDto[];
  downstreamProfileLocks: ProfileProfileLockDto[];
}

export type CreateProfileOpDto = (
  | { email: Email; entraUserId?: undefined }
  | { entraUserId: EntraUserId; email?: undefined }
) & {
  userId: UserId | undefined;
  roleIds: RoleId[];
  personalVaultDto: VaultCreationDto;
  profileDto: ProfileDto;
  adminProfileLock: ProfileProfileLockDto | undefined;
  createStartCodeAndSendMail?: boolean;
  setStartCodeEmailSender?: boolean;
};

export interface UpdateProfileOpDto {
  profileId: ProfileId;
  roleId?: RoleId;
  adminProfileLock: ProfileProfileLockDto | undefined;
  childOrganizationManagementShow?: boolean;
}

export interface RegeneratedVaultDto {
  id: VaultId;
  latestCommitId: CommitId;
  blob: Uint8Array;
  profileLocks: VaultProfileLockDto[];
  messagePublicKey?: Uint8Array;
  deleteMessageIds?: VaultMessageId[];
  regeneratedMessages?: {
    id: VaultMessageId;
    encryptedData?: Uint8Array;
  }[];
}

export interface VaultProfileLockDto {
  readonly lockingProfileId: ProfileId;
  readonly lockingProfileKeyGenerationId: ProfileKeyGenerationId;
  readonly encryptedStorableVaultKey: Uint8Array;
  readonly encryptedHighSecurityVaultKey: Uint8Array;
  readonly encryptedVaultMessagePrivateKey: Uint8Array | undefined;
}

export interface ProfileAuthenticatorLockDto {
  readonly authenticatorId: AuthenticatorId;
  readonly encryptedHighSecurityProfileSeed: Uint8Array;
  readonly encryptedStorableProfileSeed: Uint8Array;
}

export interface UpstreamProfileProfileLockDto {
  readonly lockingProfileId: ProfileId;
  readonly lockingProfileKeyGenerationId: ProfileKeyGenerationId;
  readonly encryptedHighSecurityProfileSeed: Uint8Array;
  readonly encryptedStorableProfileSeed: Uint8Array;
}

export interface NewProfileAuthenticatorLockDto {
  readonly authenticatorId: AuthenticatorId;
  readonly profileId: ProfileId;
  readonly profileKeyGenerationId: ProfileKeyGenerationId;
  readonly encryptedHighSecurityProfileSeed: Uint8Array;
  readonly encryptedStorableProfileSeed: Uint8Array;
}

export interface ProfileProfileLockDto {
  readonly profileId: ProfileId;
  readonly profileKeyGenerationId: ProfileKeyGenerationId;
  readonly lockingProfileId: ProfileId;
  readonly lockingProfileKeyGenerationId: ProfileKeyGenerationId;
  readonly encryptedHighSecurityProfileSeed: Uint8Array;
  readonly encryptedStorableProfileSeed: Uint8Array;
}

export interface NewLockDto {
  readonly encryptedStorableVaultKey: Uint8Array;
  readonly encryptedHighSecurityVaultKey: Uint8Array;
  readonly encryptedVaultMessagePrivateKey: Uint8Array;
}

export interface ForeignAuthenticatorDto {
  readonly id: AuthenticatorId;
  readonly highSecurityIdentitySigPubKey: Uint8Array;
  readonly highSecurityProfileSeedEncPubKey: Uint8Array;
  readonly highSecurityProfileSeedEncPubKeySignature: Uint8Array;
  readonly storableSigPubKey: Uint8Array;
  readonly storableSigPubKeySignature: Uint8Array;
  readonly storableProfileSeedEncPubKey: Uint8Array;
  readonly storableProfileSeedEncPubKeySignature: Uint8Array;
}

export interface ForeignProfileDto {
  readonly id: ProfileId;
  readonly keyGenerationId: ProfileKeyGenerationId;
  readonly organizationId: OrganizationId | undefined;
  readonly email: Email | undefined;
  readonly highSecurityIdentitySigPubKey: Uint8Array;
  readonly highSecurityVaultKeyEncPubKey: Uint8Array;
  readonly highSecurityVaultKeyEncPubKeySignature: Uint8Array;
  readonly highSecurityProfileSeedEncPubKey: Uint8Array;
  readonly highSecurityProfileSeedEncPubKeySignature: Uint8Array;
  readonly storableSigPubKey: Uint8Array;
  readonly storableSigPubKeySignature: Uint8Array;
  readonly storableVaultKeyEncPubKey: Uint8Array;
  readonly storableVaultKeyEncPubKeySignature: Uint8Array;
  readonly storableProfileSeedEncPubKey: Uint8Array;
  readonly storableProfileSeedEncPubKeySignature: Uint8Array;
}

export interface ProfileDto {
  readonly id: ProfileId;
  readonly profileType: ProfileType;
  readonly keyGenerationId: ProfileKeyGenerationId;
  readonly highSecurityIdentitySigPubKey: Uint8Array;
  readonly highSecurityVaultKeyEncPubKey: Uint8Array;
  readonly highSecurityVaultKeyEncPubKeySignature: Uint8Array;
  readonly highSecurityProfileSeedEncPubKey: Uint8Array;
  readonly highSecurityProfileSeedEncPubKeySignature: Uint8Array;
  readonly storableSigPubKey: Uint8Array;
  readonly storableSigPubKeySignature: Uint8Array;
  readonly storableVaultKeyEncPubKey: Uint8Array;
  readonly storableVaultKeyEncPubKeySignature: Uint8Array;
  readonly storableProfileSeedEncPubKey: Uint8Array;
  readonly storableProfileSeedEncPubKeySignature: Uint8Array;
  readonly authenticatorLocks: ProfileAuthenticatorLockDto[];
  readonly upstreamProfileLocks: UpstreamProfileProfileLockDto[];
}

export interface AuthenticatorDto {
  readonly id: AuthenticatorId;
  readonly authenticatorType: AuthenticatorType;
  readonly highSecurityLoginSigPubKey: Uint8Array;
  readonly highSecurityIdentitySigPubKey: Uint8Array;
  readonly highSecurityProfileSeedEncPubKey: Uint8Array;
  readonly highSecurityProfileSeedEncPubKeySignature: Uint8Array;
  readonly storableSigPubKey: Uint8Array;
  readonly storableSigPubKeySignature: Uint8Array;
  readonly storableProfileSeedEncPubKey: Uint8Array;
  readonly storableProfileSeedEncPubKeySignature: Uint8Array;
  readonly secretInfo: string;
  readonly secretSalt: Uint8Array;
  readonly webauthn?: AuthenticatorWebauthnDto;
}

export interface AuthenticatorWebauthnDto {
  readonly webauthnId: WebauthnId;
  readonly prfSalt: Uint8Array;
}

export interface SessionUnlockAuthenticatorCreationDto {
  readonly requestId: string;
  readonly responseJson: string;
}

export interface WebauthnAuthenticatorCreationDto {
  readonly webauthnId: WebauthnId;
  readonly prfSalt: Uint8Array;
  readonly prfSupportStatus?: WebauthnPrfSupportStatus;
  readonly registerRequestId?: string;
  readonly responseJson?: string;
}

export interface AuthenticatorCreationDto {
  readonly data: AuthenticatorDto;
  readonly profileLocks: NewProfileAuthenticatorLockDto[];
  readonly webauthn?: WebauthnAuthenticatorCreationDto;
}

export interface CommitDto {
  readonly id: CommitId;
  readonly parentId: CommitId | undefined;
  readonly blob: Uint8Array;
}

export interface LoginChallengeData {
  userId: UserId;
  challenge: string;
  authenticators: {
    id: AuthenticatorId;
    authenticatorType: AuthenticatorType;
    secretInfo: string;
    webauthn?: {
      webauthnId: WebauthnId;
      prfSalt: Uint8Array;
    };
  }[];
}

export interface LoginLongPollChannelChallengeResponse {
  userId: UserId;
  challenge: string;
  authenticator: {
    id: AuthenticatorId;
    authenticatorType: AuthenticatorType;
    secretInfo: string;
  };
  authenticatorReply: Uint8Array;
}

export { ChannelRecipient };

export interface BillingAddressDto {
  streetAndNumber?: string;
  streetAdditional?: string;
  postalCode?: string;
  city?: string;
  region?: string;
  country?: string;
}

export interface SyncUpdateVaultProfile {
  id: ProfileId;
}

// source of truth: ../../../protos/sync_update.proto
export interface SyncUpdateVault {
  id: VaultId;
  commitId: CommitId;
  generationId: GenerationId;
  profileKeyGenerationId: ProfileKeyGenerationId | undefined;
  adminProfileKeyGenerationId: ProfileKeyGenerationId | undefined;
  associatedVaultId: VaultId | undefined;
  organizationId: OrganizationId | undefined;
  profiles: Array<SyncUpdateVaultProfile>;
  vaultType: VaultType;
  dirty: boolean;
  regenerateOnSync: boolean;
  messagePublicKey: Uint8Array | undefined;
  hasMessagesQueued: boolean;
  inboxSlug: string | undefined;
  inboxEnabled: boolean;
  memberSyncMode: VaultMemberSyncMode;
  entraGroupId: EntraGroupId | undefined;
  requiredMaintenanceTasks: MaintenanceTask[];
}

export interface SyncUpdateSession {
  id: SessionId;
  createdAt: Date;
  lastUsedAt: Date;
  unlockedAt?: Date;
  unlockedUntil?: Date;
  unlockExtendAt?: Date;
  unlockRequestedAt?: Date;
  clientSettings: string | undefined;
  sessionType: SessionType;
  enabledProfileIds: Array<ProfileId>;
  unlockTimeLimitMinutes: number;
}

export interface SyncUpdateChannel {
  id: ChannelId;
  channelType: string;
}

export interface SyncUpdateSessionUnlock {
  authenticatorId: AuthenticatorId;
  encryptedSecret: Uint8Array;
}

export interface SyncUpdateOrganizationProfile {
  profileId: ProfileId;
  isNotConnected: boolean;
  isDelegatedAdmin: boolean;
  isService: boolean;
  personalVaultId?: VaultId;
  preliminaryUserId?: UserId;
  startCode?: string;
  startCodeGeneratedAt?: Date;
  startCodeEmailSentAt?: Date;
  startCodeExpiredAt?: Date;
  primaryLoginDevice?: PrimaryLoginDevice;
  lastActiveAt?: Date;
  entraUserId?: EntraUserId;
  childOrganizationManagementShow: boolean;
  roleIds: RoleId[];
}

// see https://developer.paddle.com/reference/ZG9jOjI1MzU0MDI2-subscription-status-reference
// NOTE: this is not the same data structure, but a slightly transformed view model
export type SubscriptionStatus =
  | {
      status: 'trial';
      trialEndDate: Date;
    }
  | {
      status: 'paddleActive';
      freeMembers: number;
      nextBillDate: Date;
      baseUrl: string;
      email: string;
    }
  | {
      status: 'paddlePastDue';
      freeMembers: number;
      nextBillDate: Date;
      baseUrl: string;
    }
  | {
      status: 'paddlePausedDelinquent';
      freeMembers: number;
      baseUrl: string;
      pausedAt: Date;
      pausedFrom: Date;
    }
  | {
      status: 'paddlePausedVoluntary';
      freeMembers: number;
      baseUrl: string;
      pausedAt: Date;
      pausedFrom: Date;
    }
  | {
      status: 'paddleDeleted';
      freeMembers: number;
      cancellationEffectiveDate: Date;
    }
  | {
      status: 'activeFree';
      freeMembers: number;
    }
  | {
      status: 'custom';
      left: string;
      right: string;
    }
  | {
      status: 'example';
      left: string;
      right: string;
    };

export interface SyncUpdateOrganizationAdminInfo {
  adminProfile: SyncUpdateProfile;
  entraTenantId?: EntraTenantId;
  entraPreferUserPrincipalName: boolean;
  entraSettings?: string;
  entraSynchronizedAt?: Date;
  csvIntegrationActive: boolean;
  googleWorkspaceIntegrationActive: boolean;
  trialEndDate?: Date;
  licenseType: LicenseType;
  parentOrganizationAdminProfileId?: ProfileId;
  billingOrganizationId?: OrganizationId;
}
export interface SyncUpdateOrganizationLoginSummaryVaultInfo {
  id: VaultId;
  generationId: GenerationId;
  messagePublicKey: Uint8Array;
}

export { ExportPermission, MaximumAssignableVaultPermission };

export interface RolePermissionsDto {
  // Permissions that require privileged access (at least in some cases)
  adminCanAssignTeamPermissionOther: MaximumAssignableVaultPermission;
  adminCanAssignTeamPermissionSelf: MaximumAssignableVaultPermission;
  adminCanChangeSettings: boolean;
  adminCanManageOrganization: boolean;
  adminCanViewForeignPersonalVaults: boolean;
  childOrganizationAccess: ChildOrganizationAccess;
  exportVaults: ExportPermission;
  // Permissions that don't required privileged access
  canAccessPersonalVault: boolean;
  canChangePlan: boolean;
  canConnectUsers: boolean;
  canCreateChildOrganizations: boolean;
  canCreateTeams: boolean;
  canViewAuditlog: boolean;
  canViewMonitoring: boolean;
  canViewUsers: boolean;
}

export interface RoleDto {
  id: RoleId;
  roleType: RoleType;
  customName?: string;
  changedAt: Date;

  isPrivileged: boolean;

  permissions: RolePermissionsDto;
}

export interface SyncUpdateOrganization {
  id: OrganizationId;
  name: string;
  icon: string | undefined;
  organizationType: OrganizationType;
  restrictPersonalLogins: boolean;
  unlockTimeLimit: UnlockTimeLimit;
  auditlogEnable: boolean;
  roleManagementEnable: boolean;
  childOrganizationManagementEnable: boolean;
  profileChildOrganizationManagementShow: boolean;
  serviceUserEnable: boolean;
  monitoringEnable: boolean;
  isPreliminary: boolean;
  isDelegatedAdmin: boolean;
  superUserId: UserId;
  adminProfileId: ProfileId;
  profiles: Array<SyncUpdateOrganizationProfile>;
  adminInfo?: SyncUpdateOrganizationAdminInfo;
  subscriptionError: SubscriptionErrorKnownValue | undefined;
  verifiedDomain: string | undefined;
  passwordPolicy: string | undefined;
  clientSettings: string | undefined;
  parentOrganizationInfo?: {
    id: OrganizationId;
    name: string;
    icon: string | undefined;
    metadata: string | undefined;
  };
  requiredMaintenanceTasks: MaintenanceTask[];
  entraUserId?: EntraUserId;
  loginSummaryVaultInfo?: SyncUpdateOrganizationLoginSummaryVaultInfo;
  roles: RoleDto[];
  roleIds: RoleId[];
}

export interface SyncUpdateProfileAuthenticatorLock {
  authenticatorId: AuthenticatorId;
  encryptedHighSecurityProfileSeed: Uint8Array;
  encryptedStorableProfileSeed: Uint8Array;
}

export interface SyncUpdateProfileProfileLock {
  lockingProfileId: ProfileId;
  lockingProfileKeyGenerationId: ProfileKeyGenerationId;
  encryptedHighSecurityProfileSeed: Uint8Array;
  encryptedStorableProfileSeed: Uint8Array;
}

export interface SyncUpdateProfile {
  id: ProfileId;
  keyGenerationId: ProfileKeyGenerationId;
  profileType: ProfileType;
  email: Email | undefined;
  unverifiedEmail: Email | undefined;

  organizationId: OrganizationId | undefined;

  highSecurityIdentitySigPubKey: Uint8Array;
  highSecurityVaultKeyEncPubKey: Uint8Array;
  highSecurityVaultKeyEncPubKeySignature: Uint8Array;
  highSecurityProfileSeedEncPubKey: Uint8Array;
  highSecurityProfileSeedEncPubKeySignature: Uint8Array;
  storableSigPubKey: Uint8Array;
  storableSigPubKeySignature: Uint8Array;
  storableVaultKeyEncPubKey: Uint8Array;
  storableVaultKeyEncPubKeySignature: Uint8Array;
  storableProfileSeedEncPubKey: Uint8Array;
  storableProfileSeedEncPubKeySignature: Uint8Array;

  authenticatorLocks: SyncUpdateProfileAuthenticatorLock[];
  profileLock: SyncUpdateProfileProfileLock | undefined;
}

export interface SyncUpdatePreliminaryProfile {
  id: ProfileId;
  email: Email | undefined;
  organizationId: OrganizationId | undefined;
}

export interface SyncUpdateRelatedProfile {
  id: ProfileId;
  email: Email | undefined;
}

export interface SyncAuthenticator {
  id: AuthenticatorId;
  authenticatorType: AuthenticatorType;
  highSecurityLoginSigPubKey: Uint8Array;
  highSecurityIdentitySigPubKey: Uint8Array;
  highSecurityProfileSeedEncPubKey: Uint8Array;
  highSecurityProfileSeedEncPubKeySignature: Uint8Array;
  storableSigPubKey: Uint8Array;
  storableSigPubKeySignature: Uint8Array;
  storableProfileSeedEncPubKey: Uint8Array;
  storableProfileSeedEncPubKeySignature: Uint8Array;
  secretInfo: string;
  secretSalt: Uint8Array;
}

export interface SyncUpdateWebauthnCredential {
  webauthnId: WebauthnId;
  credentialType: CredentialType;
  aaguid: string | undefined;
  // set iff credentialType is CredentialTypePasskey
  sessionId: SessionId | undefined;
  createdAt: Date;
  lastUsedAt: Date;
  prfSupportStatus: WebauthnPrfSupportStatus;
}

export interface SyncUpdateUser {
  preliminaryEmail: Email | undefined;
  preferredLocale: string | undefined;
  serverSideSettings: string | undefined;
  authenticatorBlockHash: Uint8Array | undefined;
  webauthnCredentials: SyncUpdateWebauthnCredential[];
  userClientSettings: string | undefined;
  openUserConfirmationIds: UserConfirmationId[];
  profilesLocked: boolean;
}

export interface SyncUpdateMaintenanceSettings {
  allowedTasks: MaintenanceTask[];
}

export interface SyncUpdate {
  serverTime: Date;
  syncVersion: number;
  tokenRefreshNeeded: boolean;
  clientOutdated: boolean;
  leakedPasswordsChangeTime?: Date;

  // mandatory (empty if undefined)
  sessionUnlock?: SyncUpdateSessionUnlock;

  // optional (unchanged if undefined)
  user?: SyncUpdateUser;
  sessions?: SyncUpdateSession[];
  vaults?: SyncUpdateVault[];
  achievementIds?: AchievementId[];
  channels?: SyncUpdateChannel[];
  organizations?: SyncUpdateOrganization[];
  relatedProfiles?: SyncUpdateRelatedProfile[];
  profiles?: SyncUpdateProfile[];
  preliminaryProfiles?: SyncUpdatePreliminaryProfile[];
  maintenanceSettings?: SyncUpdateMaintenanceSettings;
}

export type EncryptedSecretAuthenticatorReplyDto = {
  encryptedSecret: Uint8Array;
  registration: boolean;
};

export interface TestingEmailDto {
  to: Email;
  template: string;
  data: { [k: string]: string };
}

export interface AuthenticatorBlockDto {
  blob: Uint8Array;
  signature: Uint8Array;
}

export interface AuthenticatorListDto {
  authenticators: AuthenticatorDto[];
  blocks: AuthenticatorBlockDto[];
}

export interface PaymentDto {
  eventTime: Date;
  receiptUrl: string;
  saleGrossCents: number;
  paymentTaxCents: number;
  currency: string;
  orderId: string;
}

export interface UpcomingPaymentDto {
  currency: string;
  date: Date;
  amountCents: number;
}

export interface SubscriptionDetailsDto {
  payments: PaymentDto[];
  paddleSubscription:
    | {
        subscriptionPlan: SubscriptionPlan;
        quantity: number;
        unitPriceCents: number;
        totalPriceCents: number;
        currency: string;
        nextBillDate?: Date;
      }
    | undefined;
  status: SubscriptionStatus | undefined;
  billableUsers: number;
}

export interface EntraUserDto {
  id: EntraUserId;
  email: string;
  groups: string[];
  matchesSyncFilter: boolean;
  accountEnabled: boolean;
}

export interface EntraGroupDto {
  id: EntraGroupId;
  name: string;
}

export interface EntraTenantDto {
  isUnauthorized: boolean;
  users: EntraUserDto[];
  groups: EntraGroupDto[];
  synchronizedAt: Date | undefined;
}

export interface GoogleWorkspaceUserDto {
  id: GoogleWorkspaceUserId;
  email: Email;
}

export interface GoogleWorkspaceIntegrationDto {
  updatedAt: Date;
  users: GoogleWorkspaceUserDto[];
}

// see backend/protos/auditlog_service.proto for explanation
export interface AuditlogEventFilter {
  fromTime?: Date;
  toTime?: Date;
  eventTypes?: AuditlogEventType[];
  loginIds?: LoginId[];
  profileIds?: ProfileId[];
  targetProfileIds?: ProfileId[];
  vaultIds?: VaultId[];
  ipPrefix?: string;
  additionalFilter?: {
    loginIds: LoginId[];
    profileIds: ProfileId[];
    targetProfileIds: ProfileId[];
    vaultIds: VaultId[];
    ipPrefix: string;
  };
}

export interface VaultMessageDto {
  vaultId: VaultId;
  generationId: GenerationId;
  type: VaultMessageTypeKnownValue;
  encryptedData: Uint8Array;
}

export interface ShareLinkUpdateDto {
  id: ShareLinkId;
  encryptedPayload?: Uint8Array;
  encryptedProtectedSecret?: Uint8Array;
  expiresAt?: Date;
}

export interface ShareSecretUpdateDto {
  id: ShareSecretId;
  encryptedPayload?: Uint8Array;
}

export interface RegistrationInfo {
  status: RegistrationInfoStatus;
  organization:
    | {
        id: OrganizationId;
        name: string;
        icon: string | undefined;
        startCodePrefix: string | undefined;
        profileId: ProfileId | undefined;
        organizationType: OrganizationType;
      }
    | undefined;
  webauthnAllowed: boolean;
  profileType: ProfileType;
}

export interface UserConfirmationData {
  type: UserConfirmationType;
  loginId?: LoginId;
  vaultId?: VaultId;
  profileId?: ProfileId;
  organizationId?: OrganizationId;
}

export interface BreachModel {
  name: string;
  title: string;
  breachDate: Date;
  addedDate: Date;
  dataClasses: string[];
  logoFileName: string;
}

export interface BreachData {
  domains: {
    domain: string;
    breaches: {
      model: BreachModel;
      aliases: string[];
    }[];
  }[];
  lastCheckDate: Date;
  totalPwnCountBillions: number;
  totalBreachCount: number;
}

export type ChildOrganizationLicense =
  | { type: LicenseType.UNKNOWN }
  | { type: LicenseType.EXAMPLE }
  | { type: LicenseType.NONE; trialEndDate: Date }
  | {
      type: LicenseType.VIA_MSP;
      contractStartDate: Date;
      contractEndDate: Date;
      licenseCount: number;
    };

export interface ChildOrganization {
  id: OrganizationId;
  name: string;
  users: {
    isAdmin: boolean;
    isNotConnected: boolean;
    hasStartCode: boolean;
    lastActiveAd?: Date;
  }[];
  delegatedAdmins: {
    profileId: ProfileId;
    parentOrganizationProfileId: ProfileId;
    personalVaultId?: VaultId;
  }[];
  createdAt: Date;
  parentOrganizationMetadata?: string;
  adminProfile?: SyncUpdateProfile;
  license: ChildOrganizationLicense;
}

export interface LoginMaintenanceCriteriaDto {
  noLogin?: boolean;
  leakedPasswordsMinChangeTime?: Date;
  loginSummaryMinVersion?: number;
  passwordPolicyMinChangeTime?: Date;
}

export interface DebugEntraUserDto {
  id: EntraUserId;
  mail: Email;
  enabled: boolean;
}

export interface DebugEntraGroupDto {
  id: EntraGroupId;
  name: string;
  userIds: EntraUserId[];
}

export function authenticatorTypeToDisplayName(t: AuthenticatorType): string {
  switch (t) {
    case AuthenticatorType.PUSH:
      return 'push';
    case AuthenticatorType.BACKUP_CODE:
      return 'backup-code';
    case AuthenticatorType.BACKUP_OS:
      return 'backup-os';
    case AuthenticatorType.DUMMY:
      return 'dummy';
    case AuthenticatorType.SESSION_UNLOCK:
      return 'session-unlock';
    case AuthenticatorType.WEBAUTHN:
      return 'webauthn';
    default:
      return 'unknown';
  }
}

export function toVaultProfileLockDto(
  pb: VaultProfileLock | undefined,
): VaultProfileLockDto | undefined {
  if (!pb) {
    return undefined;
  }

  let encryptedVaultMessagePrivateKey: Uint8Array | undefined = undefined;
  if (pb.encryptedVaultMessagePrivateKey.length > 0) {
    encryptedVaultMessagePrivateKey = pb.encryptedVaultMessagePrivateKey;
  }
  return {
    lockingProfileId: requireFieldNotEmpty(pb.lockingProfileId),
    lockingProfileKeyGenerationId: requireFieldNotEmpty(pb.lockingProfileKeyGenerationId),
    encryptedStorableVaultKey: pb.encryptedStorableVaultKey,
    encryptedHighSecurityVaultKey: pb.encryptedHighSecurityVaultKey,
    encryptedVaultMessagePrivateKey,
  };
}

export function toPbVaultProfileLock(dto: VaultProfileLockDto): VaultProfileLock {
  return create(VaultProfileLockSchema, {
    ...dto,
    encryptedVaultMessagePrivateKey: dto.encryptedVaultMessagePrivateKey,
  });
}

export function toPbVaultLock(l: VaultLockCreationDto): VaultLockCreationData {
  return create(VaultLockCreationDataSchema, {
    ...l,
    lock: toPbVaultProfileLock(l.lock),
    associatedLock: l.associatedLock ? toPbVaultProfileLock(l.associatedLock) : undefined,
  });
}

export function toPbProfileRegenerateData(p: RegeneratedProfileDto): ProfileRegenerateData {
  return create(ProfileRegenerateDataSchema, {
    profile: p.profile,
    vaultLocks: p.vaultLocks.map(toPbVaultLock),
    downstreamProfileLocks: p.downstreamProfileLocks,
  });
}
